/**
 * useI18nInspector
 *
 * Superuser-only tool that enables "translation inspect mode" on the frontend.
 * When active, hovering over any translated string shows the i18n key as a
 * tooltip. Clicking navigates directly to the correct key in the backend
 * translation manager (/api/v2/manage/frontend/{locale}?key=…&focus=1).
 *
 * Activate: Cmd+Shift+T (Mac) / Ctrl+Shift+T (Win/Linux)  — superusers only.
 *
 * How it works (zero component changes required):
 *  1. At composable init time, the full locale message tree is flattened to a
 *     reverse map: { locale: { translatedValue: 'dot.notation.key' } }.
 *  2. When inspect mode is ON, mousemove events walk the DOM upward from the
 *     hovered element looking for an element whose trimmed textContent is a
 *     single known translation value. A floating tooltip is injected showing
 *     the key with a click-to-edit link.
 *  3. A single-click on any highlighted element opens the manage-UI URL with
 *     the correct locale, key, and focus=1 params.
 *  4. The active locale (Phase 3) is read from i18n.global.locale so the
 *     correct language tab always opens.
 */

import { computed, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { getAppEnvironment, getRuntimeConfigValue } from '@/app/runtime';
import i18n from '@/i18n';
import { useAuthStore } from '@/stores/useAuthStore';

// ---------------------------------------------------------------------------
// Module-level reverse-lookup map (built once per inspect-mode activation)
// { locale: { 'translated value': 'dot.notation.key' } }
// ---------------------------------------------------------------------------

const _reverseLookup = {};

/**
 * Recursively collect all dot-notation leaf keys from a message tree.
 * Treats both strings AND compiled message functions as leaves.
 */
function _collectKeys(prefix, obj, keys) {
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (typeof v === 'string' || typeof v === 'function') {
      keys.push(key);
    } else if (v && typeof v === 'object') {
      _collectKeys(key, v, keys);
    }
  }
}

/**
 * Build (or rebuild) the reverse map for all locales.
 * Uses i18n.global.t() to resolve pre-compiled message functions correctly.
 */
function _buildMap() {
  const messages = i18n.global.messages?.value ?? {};
  for (const [locale, messageMap] of Object.entries(messages)) {
    _reverseLookup[locale] = {};
    const keys = [];
    _collectKeys('', messageMap, keys);
    for (const key of keys) {
      try {
        // t() handles both plain strings and compiled message functions
        const str = i18n.global.t(key, {}, { locale });
        if (str && typeof str === 'string' && !str.includes('{') && str !== key) {
          _reverseLookup[locale][str] = key;
          // Also store a whitespace-normalised version
          const norm = str.replace(/\s+/g, ' ').trim();
          if (norm !== str) _reverseLookup[locale][norm] = key;
        }
      } catch (_) {
        // skip keys that fail to resolve (e.g. parametric messages)
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Shared state (singleton so multiple callers share one inspector)
// ---------------------------------------------------------------------------

const isInspectMode = ref(
  typeof sessionStorage !== 'undefined' && sessionStorage.getItem('i18n_inspect_mode') === 'true'
);

// ---------------------------------------------------------------------------
// Tooltip DOM element
// ---------------------------------------------------------------------------

let _tooltipEl = null;
let _lastHighlighted = null;

function _getTooltip() {
  if (_tooltipEl) return _tooltipEl;
  _tooltipEl = document.createElement('div');
  _tooltipEl.id = 'i18n-inspector-tooltip';
  _tooltipEl.style.cssText = [
    'position:fixed',
    'z-index:99999',
    'background:rgb(var(--color-border-subtle))',
    'color:rgb(var(--color-bg-subtle))',
    'font-family:ui-monospace,monospace',
    'font-size:12px',
    'line-height:1.4',
    'padding:6px 10px',
    'border-radius:6px',
    'box-shadow:0 4px 16px rgb(var(--color-static-black) / .4)',
    'pointer-events:none',
    'max-width:320px',
    'word-break:break-all',
    'border-left:3px solid rgb(var(--color-state-warning))',
    'display:none',
  ].join(';');
  document.body.appendChild(_tooltipEl);
  return _tooltipEl;
}

function _showTooltip(x, y, label, subtitle = null, accentColor = 'rgb(var(--color-state-warning))') {
  const tip = _getTooltip();
  tip.style.borderLeftColor = accentColor;
  const subtitleHtml = subtitle
    ? `<br><span style="color:rgb(var(--color-text-muted));font-size:11px">${_escHtml(subtitle)}</span>`
    : '';
  tip.innerHTML = `<span style="color:${accentColor};font-weight:600">${_escHtml(
    label
  )}</span>${subtitleHtml}<br><span style="color:rgb(var(--color-text-muted));font-size:11px">Click to edit · press <kbd style="font-family:inherit;background:rgb(var(--color-border-default));color:rgb(var(--color-border-subtle));border-radius:3px;padding:0 4px">Esc</kbd> to exit</span>`;
  tip.style.display = 'block';

  // Position: prefer above-right of cursor, flip if near edge
  const margin = 12;
  let left = x + margin;
  let top = y - 48;
  if (left + 320 > window.innerWidth) left = x - 320 - margin;
  if (top < 8) top = y + margin;
  tip.style.left = `${left}px`;
  tip.style.top = `${top}px`;
}

function _hideTooltip() {
  if (_tooltipEl) _tooltipEl.style.display = 'none';
}

function _escHtml(s) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// ---------------------------------------------------------------------------
// DOM helpers
// ---------------------------------------------------------------------------

/**
 * Walk up from `el` (max 5 levels) looking for an element whose visible text
 * (innerText) exactly matches a single known translation value.
 * Returns { key, el } or null.
 */
function _findKeyAtElement(el, locale) {
  const map = _reverseLookup[locale] ?? _reverseLookup.en ?? {};
  let node = el;
  for (let i = 0; i < 5 && node && node !== document.body; i++) {
    // innerText reflects visible text only (ignores SVG paths, hidden nodes)
    const text = (node.innerText ?? node.textContent ?? '').trim();
    if (text && text.length > 0 && text.length < 500) {
      if (map[text]) return { key: map[text], el: node };
      const normalized = text.replace(/\s+/g, ' ');
      if (map[normalized]) return { key: map[normalized], el: node };
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Walk up from `el` (max 5 levels) looking for a form control whose
 * `placeholder` or `aria-label` attribute matches a known translation value.
 * Returns { key, el } or null.
 * This covers <input>, <textarea>, and <select> elements.
 */
function _findFormKeyAtElement(el, locale) {
  const map = _reverseLookup[locale] ?? _reverseLookup.en ?? {};
  let node = el;
  for (let i = 0; i < 5 && node && node !== document.body; i++) {
    const tag = node.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') {
      const placeholder = node.getAttribute('placeholder');
      if (placeholder && map[placeholder]) {
        return { key: map[placeholder], el: node };
      }
      const ariaLabel = node.getAttribute('aria-label');
      if (ariaLabel && map[ariaLabel]) {
        return { key: map[ariaLabel], el: node };
      }
    }
    node = node.parentElement;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Admin-portal deep-link builder
//
// The "Inspect" overlay used to open the legacy `/api/v2/manage/...` backend
// pages. Those were replaced by the standalone admin SPA, which lives on its
// own origin (admin.*), so it can't read this app's session. We deep-link into
// the admin's hash routes and hand off the current tokens via the URL; the
// admin entry (src/admin/main.js) consumes + strips them before its auth guard
// runs, so the link arrives already authenticated.
// ---------------------------------------------------------------------------

/**
 * Per-environment admin base URL. Overridable via `VUE_APP_ADMIN_URL` (build-
 * time env or runtime-config injection); otherwise mapped from the app
 * environment: dev → :8081, acceptance/production → the admin.* subdomain.
 */
function _adminBaseUrl() {
  const override = getRuntimeConfigValue('VUE_APP_ADMIN_URL', process.env.VUE_APP_ADMIN_URL);
  if (override) return String(override).replace(/\/$/, '');
  const env = String(getAppEnvironment() || '').toLowerCase();
  if (env === 'production') return 'https://admin.profiteia.io';
  if (env === 'acceptance') return 'https://admin.acc.profiteia.io';
  return 'http://localhost:8081';
}

/**
 * One-time token handoff so the deep-link survives the admin auth guard without
 * a fresh login. Returns `[key, value]` pairs for the current session tokens
 * (omitted if absent — the admin then falls back to its own session).
 */
function _handoffPairs() {
  const pairs = [];
  const token = sessionStorage.getItem('accessToken');
  const refresh = localStorage.getItem('refreshToken');
  if (token) pairs.push(['token', token]);
  if (refresh) pairs.push(['refresh_token', refresh]);
  return pairs;
}

// Build a query string with `encodeURIComponent` (NOT URLSearchParams, which
// encodes spaces as `+`; vue-router does not decode `+` back to a space, so a
// translation key containing a space would arrive mangled in the admin).
function _queryString(pairs) {
  return pairs.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
}

function _buildManageUrl(locale, key) {
  const pairs = [['key', key], ['focus', '1'], ..._handoffPairs()];
  return `${_adminBaseUrl()}/#/text/${encodeURIComponent(locale)}?${_queryString(pairs)}`;
}

/**
 * Deep-link into the admin's dataset / visualization translation editor.
 * Routes: admin-text-dataset (`/text/datasets/:datasetId`) and
 * admin-text-visualization (`/text/visualizations/:visualizationId`).
 */
function _buildDatasetManageUrl(datasetId, visualizationId) {
  const pairs = [['locale', i18n.global.locale.value], ['focus', '1'], ..._handoffPairs()];
  const root = _adminBaseUrl();
  if (visualizationId) {
    return `${root}/#/text/visualizations/${encodeURIComponent(visualizationId)}?${_queryString(pairs)}`;
  }
  return `${root}/#/text/datasets/${encodeURIComponent(datasetId)}?${_queryString(pairs)}`;
}

/**
 * Walk up from `el` (max 8 levels) looking for a [data-dataset-id] ancestor.
 * Returns { datasetId, visualizationId, el } or null.
 */
function _findDatasetAtElement(el) {
  let node = el;
  for (let i = 0; i < 8 && node && node !== document.body; i++) {
    const datasetId = node.dataset?.datasetId;
    if (datasetId) {
      const visualizationId = node.dataset?.visualizationId || null;
      return { datasetId, visualizationId, el: node };
    }
    node = node.parentElement;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Event handlers (installed/removed when inspect mode toggles)
// ---------------------------------------------------------------------------

function _onMouseMove(event) {
  const locale = i18n.global.locale?.value ?? 'nl';

  // Priority: dataset/viz element > form control (placeholder/aria-label) > i18n text
  const datasetFound = _findDatasetAtElement(event.target);
  if (datasetFound) {
    if (_lastHighlighted && _lastHighlighted !== datasetFound.el) {
      _lastHighlighted.classList.remove('i18n-inspect-highlight');
    }
    datasetFound.el.classList.add('i18n-inspect-highlight');
    _lastHighlighted = datasetFound.el;
    const subtitle = datasetFound.visualizationId ? `viz: ${datasetFound.visualizationId}` : null;
    _showTooltip(event.clientX, event.clientY, `🗂 ${datasetFound.datasetId}`, subtitle, 'rgb(var(--color-brand-accent))');
    return;
  }

  // Form controls (placeholder / aria-label) take priority over text content
  const found =
    _findFormKeyAtElement(event.target, locale) ?? _findKeyAtElement(event.target, locale);

  if (found) {
    if (_lastHighlighted && _lastHighlighted !== found.el) {
      _lastHighlighted.classList.remove('i18n-inspect-highlight');
    }
    found.el.classList.add('i18n-inspect-highlight');
    _lastHighlighted = found.el;
    _showTooltip(event.clientX, event.clientY, `🔑 ${found.key}`);
  } else {
    if (_lastHighlighted) {
      _lastHighlighted.classList.remove('i18n-inspect-highlight');
      _lastHighlighted = null;
    }
    _hideTooltip();
  }
}

function _onClick(event) {
  const locale = i18n.global.locale?.value ?? 'nl';

  // Dataset/viz elements take priority
  const datasetFound = _findDatasetAtElement(event.target);
  if (datasetFound) {
    event.preventDefault();
    event.stopPropagation();
    window.open(
      _buildDatasetManageUrl(datasetFound.datasetId, datasetFound.visualizationId),
      '_blank',
      'noopener'
    );
    return;
  }

  const found =
    _findFormKeyAtElement(event.target, locale) ?? _findKeyAtElement(event.target, locale);
  if (found) {
    event.preventDefault();
    event.stopPropagation();
    window.open(_buildManageUrl(locale, found.key), '_blank', 'noopener');
  }
}

/**
 * Intercept mousedown on form controls so they don't steal focus / open
 * dropdowns while inspect mode is active.
 */
function _onMouseDown(event) {
  const locale = i18n.global.locale?.value ?? 'nl';
  const found = _findFormKeyAtElement(event.target, locale);
  if (found) {
    event.preventDefault();
    event.stopPropagation();
  }
}

function _onKeyDown(event) {
  // Cmd+Shift+T (Mac) / Ctrl+Shift+T (Win/Linux) toggles inspection off again.
  if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key === 'T') {
    event.preventDefault();
    _toggleMode();
    return;
  }
  // Escape is the obvious, discoverable way out of inspection mode.
  if (event.key === 'Escape' && isInspectMode.value) {
    event.preventDefault();
    _toggleMode();
  }
}

// ---------------------------------------------------------------------------
// Enable / disable
// ---------------------------------------------------------------------------

function _enableInspectMode() {
  // Rebuild map every time so we always have fresh, correctly-resolved strings.
  // This also handles pre-compiled message functions from @intlify/vue-i18n-loader.
  _buildMap();
  document.body.classList.add('i18n-inspect-mode');
  document.addEventListener('mousemove', _onMouseMove, { passive: true });
  // capture phase — intercept before the element handles the event
  document.addEventListener('mousedown', _onMouseDown, true);
  document.addEventListener('click', _onClick, true);
  document.addEventListener('keydown', _onKeyDown);
}

function _disableInspectMode() {
  document.body.classList.remove('i18n-inspect-mode');
  document.removeEventListener('mousemove', _onMouseMove);
  document.removeEventListener('mousedown', _onMouseDown, true);
  document.removeEventListener('click', _onClick, true);
  document.removeEventListener('keydown', _onKeyDown);
  if (_lastHighlighted) {
    _lastHighlighted.classList.remove('i18n-inspect-highlight');
    _lastHighlighted = null;
  }
  _hideTooltip();
}

function _toggleMode() {
  isInspectMode.value = !isInspectMode.value;
  sessionStorage.setItem('i18n_inspect_mode', isInspectMode.value ? 'true' : 'false');
  if (isInspectMode.value) {
    _enableInspectMode();
  } else {
    _disableInspectMode();
  }
}

// ---------------------------------------------------------------------------
// Public composable
// ---------------------------------------------------------------------------

/**
 * useI18nInspector composable.
 *
 * Usage – mount this composable in App.vue to register the keyboard shortcut
 * globally. Also import in PageHeader.vue to render the toggle button.
 *
 * @returns {{ isInspectMode: import('vue').Ref<boolean>, toggleInspectMode: () => void, manageUrlForKey: (key: string) => string }}
 */
export function useI18nInspector() {
  const authStore = useAuthStore();
  const { locale } = useI18n();

  const isSuperUser = computed(() => authStore.isSuperUser);

  /**
   * Get the manage URL for a specific i18n key using the current locale.
   */
  function manageUrlForKey(key) {
    return _buildManageUrl(locale.value, key);
  }

  /**
   * Toggle the i18n inspection mode. Only valid for superusers.
   * Calling this on a non-superuser is a no-op.
   */
  function toggleInspectMode() {
    if (!isSuperUser.value) return;
    _toggleMode();
  }

  return {
    isInspectMode,
    isSuperUser,
    toggleInspectMode,
    manageUrlForKey,
  };
}

/**
 * Mount the global keyboard shortcut listener.
 * Call this once from App.vue with `useI18nInspectorMount()`.
 * Automatically removes the listener when the component is unmounted.
 */
export function useI18nInspectorMount() {
  const authStore = useAuthStore();

  let tPressCount = 0;
  let tPressTimeout = null;

  function onKeyDown(event) {
    // Press T 5 times while holding Shift to toggle inspect mode — superusers only
    if (event.shiftKey && event.key === 'T') {
      if (!authStore.isSuperUser) return;

      tPressCount++;

      // Reset counter after 2 seconds of inactivity
      clearTimeout(tPressTimeout);
      tPressTimeout = setTimeout(() => {
        tPressCount = 0;
      }, 2000);

      if (tPressCount === 5) {
        tPressCount = 0;
        clearTimeout(tPressTimeout);
        _toggleMode();
      }
    }
  }

  // Restore active state after navigation
  if (isInspectMode.value && authStore.isSuperUser) {
    _enableInspectMode();
  }

  document.addEventListener('keydown', onKeyDown);

  onUnmounted(() => {
    document.removeEventListener('keydown', onKeyDown);
    _disableInspectMode();
  });
}
